import React, { useEffect, useState } from 'react';

import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

import {
  FormControl,
  TextField,
  FormHelperText,
  Box,
  Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { useGetKaapClusters } from '../../hooks/useGetKaapClusters';
import { KaapClusterNodeGroupValue } from './types';

const CLUSTER_GROUP_NAMES: Record<string, string> = {
  'dev-playground': 'Dev Playground - Experimental',
  'dev-internal': 'Dev Internal - For development environments',
  'prod-internal': 'Prod Internal - For internal production services',
  'prod-external':
    'Prod External - Only for production applications serving TomTom commercial clients',
};

const getClusterGroupName = (stage?: string, tier?: string): string => {
  if (!stage || !tier) {
    return tier ?? '';
  }

  return CLUSTER_GROUP_NAMES[`${stage}-${tier}`] ?? tier;
};

const capitalizeFirstLetter = (originalString: string) => {
  if (originalString.length === 0) return originalString;
  return originalString.charAt(0).toUpperCase() + originalString.slice(1);
};

export const KaapClusterNodeGroupPicker = (
  props: FieldExtensionComponentProps<KaapClusterNodeGroupValue>,
) => {
  const {
    onChange,
    schema: { title = 'Cluster & Node Groups' },
    required,
    rawErrors,
    formData,
    idSchema,
    formContext,
    uiSchema,
  } = props;

  const disabled = Boolean((uiSchema as any)?.['ui:disabled']);

  const selectedStage: string | undefined = (formContext as Record<string, any>)
    ?.formData?.stage;

  const { value: clusters, loading } = useGetKaapClusters();
  const hasError = rawErrors?.length > 0;

  const stageClusters = (clusters ?? []).filter(
    c => !selectedStage || c.stage === selectedStage,
  );

  const selectedCluster =
    stageClusters.find(c => c.id === formData?.clusterId) ?? null;

  const [selectedNodeGroups, setSelectedNodeGroups] = useState<string[]>(
    formData?.nodeGroups ?? [],
  );

  useEffect(() => {
    if (!loading && formData?.clusterId && !selectedCluster) {
      setSelectedNodeGroups([]);
      onChange({ clusterId: '', tier: '', nodeGroups: [] });
    }
  }, [selectedStage, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClusterChange = (_: any, cluster: typeof selectedCluster) => {
    setSelectedNodeGroups([]);
    if (cluster) {
      onChange({ clusterId: cluster.id, tier: cluster.tier, nodeGroups: [] });
    } else {
      onChange({ clusterId: '', tier: '', nodeGroups: [] });
    }
  };

  const handleNodeGroupsChange = (_: any, nodeGroups: string[]) => {
    setSelectedNodeGroups(nodeGroups);
    if (selectedCluster) {
      onChange({
        clusterId: selectedCluster.id,
        tier: selectedCluster.tier,
        nodeGroups,
      });
    }
  };

  useEffect(() => {
    if (formData?.nodeGroups) {
      setSelectedNodeGroups(formData.nodeGroups);
    }
  }, [formData?.clusterId, formData?.nodeGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  const availableNodeGroups = selectedCluster?.nodeGroups ?? [];

  if (disabled) {
    const clusterLabel = selectedCluster?.id ?? formData?.clusterId ?? '';
    const tierLabel = capitalizeFirstLetter(
      selectedCluster?.tier ?? formData?.tier ?? '',
    );

    return (
      <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
        <Typography variant="h6">Cluster and Node Groups</Typography>
        <FormControl fullWidth>
          <TextField
            id={`${idSchema?.$id}-tier`}
            label="Tier"
            size="small"
            value={tierLabel}
            disabled
            inputProps={{ readOnly: true }}
          />
        </FormControl>
        <FormControl fullWidth>
          <TextField
            id={`${idSchema?.$id}-cluster`}
            label={title}
            size="small"
            variant="outlined"
            value={clusterLabel}
            disabled
            inputProps={{ readOnly: true }}
          />
        </FormControl>

        <FormControl fullWidth>
          <Autocomplete
            id={`${idSchema?.$id}-nodegroups`}
            multiple
            size="small"
            value={selectedNodeGroups}
            options={selectedCluster?.nodeGroups ?? []}
            getOptionLabel={option => option}
            onChange={handleNodeGroupsChange}
            renderInput={params => (
              <TextField
                {...params}
                label="Dedicated Node Groups"
                size="small"
                variant="outlined"
                InputProps={params.InputProps}
              />
            )}
            noOptionsText="No node groups available for this cluster"
          />
          {selectedCluster && (
            <FormHelperText>
              Optional — leave empty to use only shared node groups
            </FormHelperText>
          )}
        </FormControl>

        {hasError &&
          rawErrors.map(err => (
            <FormHelperText key={err} error>
              {err}
            </FormHelperText>
          ))}
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      <Typography variant="h6">Cluster and Node Groups</Typography>
      <FormControl
        fullWidth
        required={required}
        error={hasError && !formData?.clusterId}
      >
        <Autocomplete
          id={`${idSchema?.$id}-cluster`}
          size="small"
          value={selectedCluster}
          loading={loading}
          options={[...stageClusters].sort(
            (a, b) =>
              b.tier.localeCompare(a.tier) || b.name.localeCompare(a.name),
          )}
          groupBy={option => getClusterGroupName(option.stage, option.tier)}
          getOptionLabel={option => option.id}
          getOptionSelected={(option, value) => option.id === value.id}
          onChange={handleClusterChange}
          renderOption={option => (
            <Box>
              <Typography variant="body2">{option.id}</Typography>
            </Box>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label={title}
              size="small"
              required={required}
              variant="outlined"
              InputProps={params.InputProps}
              error={hasError && !formData?.clusterId}
            />
          )}
          noOptionsText={
            selectedStage
              ? `No clusters found for stage "${selectedStage}"`
              : 'No clusters found'
          }
          loadingText="Loading clusters..."
        />
      </FormControl>

      {selectedCluster && (
        <FormControl fullWidth>
          <Autocomplete
            id={`${idSchema?.$id}-nodegroups`}
            multiple
            size="small"
            value={selectedNodeGroups}
            options={availableNodeGroups}
            getOptionLabel={option => option}
            onChange={handleNodeGroupsChange}
            renderInput={params => (
              <TextField
                {...params}
                label="Dedicated Node Groups"
                size="small"
                variant="outlined"
                InputProps={params.InputProps}
              />
            )}
            noOptionsText="No node groups available for this cluster"
          />
          <FormHelperText>
            Optional — leave empty to use only shared node groups
          </FormHelperText>
        </FormControl>
      )}

      {hasError &&
        rawErrors.map(err => (
          <FormHelperText key={err} error>
            {err}
          </FormHelperText>
        ))}
    </Box>
  );
};
